/**
 * The single place fetch/lookup analytics event names are defined.
 *
 * Keyed by the registered ngx-formly field `type` string. The forwarder maps a
 * FetchEvent's `fieldType` to the base name here; failures append `_ERROR`.
 *
 * Naming convention: `<COMPONENT_TYPE>_<ACTION>` in CAPITAL_SNAKE. Names are
 * code-level defaults — a service manager never sets them; BI filters by event
 * name + the standard properties (service_name, institution_name, etc.).
 */
export const FETCH_EVENT_NAMES: Record<string, string> = {
  customgenericdatafetch: 'CUSTOMGENERICDATAFETCH_SINGLE_FIELD_LOOKUP',
  custommultiselectdatafetch: 'CUSTOMMULTISELECTDATAFETCH_LIST_LOOKUP',
  customgenericguardeddatafetch:
    'CUSTOMGENERICGUARDEDDATAFETCH_VERIFIED_LOOKUP',
  customdirectdatafetch: 'CUSTOMDIRECTDATAFETCH_AUTO_LOOKUP',
  customconditionaldatafetch: 'CUSTOMCONDITIONALDATAFETCH_DEPENDENT_LOOKUP',
  customdropdowndatafetchpaginated:
    'CUSTOMDROPDOWNDATAFETCHPAGINATED_OPTION_LOOKUP',
  customidinput: 'CUSTOMIDINPUT_ID_VERIFICATION',
  customdoubleidinput: 'CUSTOMDOUBLEIDINPUT_DUAL_ID_VERIFICATION',
  customcrvsidinput: 'CUSTOMCRVSIDINPUT_CRVS_ID_VERIFICATION',
  // ITMED-137 additions — 4 lookups + 3 file-upload widgets
  customotpdatafetch: 'CUSTOMOTPDATAFETCH_OTP_LOOKUP',
  genericbuttonfetch: 'GENERICBUTTONFETCH_BUTTON_LOOKUP',
  customtininput: 'CUSTOMTININPUT_TIN_VERIFICATION',
  customapplicationnumberinput:
    'CUSTOMAPPLICATIONNUMBERINPUT_APPLICATION_LOOKUP',
  customfileupload: 'CUSTOMFILEUPLOAD_FILE_UPLOAD',
  externalfileupload: 'EXTERNALFILEUPLOAD_FILE_UPLOAD',
  custominternalfileupload: 'CUSTOMINTERNALFILEUPLOAD_FILE_UPLOAD',
  customdropdown: 'CUSTOMDROPDOWN_OPTIONS_LOOKUP',
  customdropdownpaginated: 'CUSTOMDROPDOWNPAGINATED_OPTIONS_LOOKUP',
  // Non-formly action: Pay button (names match AnalyticsEventType.INITIATE_PAYMENT[_ERROR]).
  payment: 'INITIATE_PAYMENT',
};

/** Fallback when a field type has no entry above. */
export const FETCH_EVENT_NAME_FALLBACK = 'LOOKUP';

/** Resolve the event name for a (fieldType, outcome) pair. */
export function resolveFetchEventName(
  fieldType: string,
  outcome: 'success' | 'failure'
): string {
  const base = FETCH_EVENT_NAMES[fieldType] ?? FETCH_EVENT_NAME_FALLBACK;
  return outcome === 'failure' ? `${base}_ERROR` : base;
}
